/**
 * The browser consent cookie (DC-1) — a pure module with no React dependency, so the Playwright
 * config can import it to seed the cookie into every e2e browser context.
 *
 * The cookie is first-party and host-only with Path=/, so web, tenant and landlord on the shared
 * origin all read the same decision (BR-10). It is readable by client script (not HttpOnly).
 */
import { COOKIE_NOTICE_VERSION } from '@resida/api-types/cookie-consent';
export const COOKIE_CONSENT_COOKIE_NAME = 'resida_cookie_consent';
export { COOKIE_NOTICE_VERSION };
/** 12 months (NFR-RETENTION-2). */
export const COOKIE_CONSENT_MAX_AGE_SECONDS = 31536000;
const DECISIONS = ['granted', 'denied'];
/**
 * A decision is current when it was made under the current notice version and less than
 * 12 months ago (BR-9). Shared by the cookie decoder and the server-record reconciliation.
 */
export function isConsentCurrent(decision, now = new Date()) {
    if (decision.noticeVersion !== COOKIE_NOTICE_VERSION)
        return false;
    const decided = new Date(decision.decidedAt);
    if (Number.isNaN(decided.getTime()))
        return false;
    const expires = new Date(decided.getTime());
    expires.setUTCFullYear(expires.getUTCFullYear() + 1);
    return now.getTime() < expires.getTime();
}
export function encodeConsentCookieValue(stored) {
    return encodeURIComponent(JSON.stringify(stored));
}
/**
 * Decode a raw cookie value. Returns null — "no decision" — when it is malformed, on another
 * schema version, has an unknown decision, was made under another notice version, or is more
 * than 12 months old (BR-9, NFR-SECURITY-3).
 */
export function decodeConsentCookieValue(raw, now = new Date()) {
    let parsed;
    try {
        parsed = JSON.parse(decodeURIComponent(raw));
    }
    catch {
        // A tampered or truncated cookie is treated as absent by design (BR-9) — not an error.
        return null;
    }
    if (typeof parsed !== 'object' || parsed === null)
        return null;
    const value = parsed;
    if (value.v !== 1)
        return null;
    if (typeof value.analytics !== 'string' || !DECISIONS.includes(value.analytics))
        return null;
    if (typeof value.noticeVersion !== 'string' || typeof value.decidedAt !== 'string')
        return null;
    const stored = {
        v: 1,
        analytics: value.analytics,
        noticeVersion: value.noticeVersion,
        decidedAt: value.decidedAt,
    };
    return isConsentCurrent(stored, now) ? stored : null;
}
/** The valid stored decision in this browser, or null (always null during server render). */
export function readStoredConsent(now = new Date()) {
    if (typeof document === 'undefined')
        return null;
    const prefix = `${COOKIE_CONSENT_COOKIE_NAME}=`;
    const entry = document.cookie
        .split(';')
        .map((part) => part.trim())
        .find((part) => part.startsWith(prefix));
    return entry ? decodeConsentCookieValue(entry.slice(prefix.length), now) : null;
}
/** Write the decision: host-only (no Domain), Path=/, 12 months, SameSite=Lax, Secure. */
export function writeStoredConsent(stored) {
    if (typeof document === 'undefined')
        return;
    document.cookie =
        `${COOKIE_CONSENT_COOKIE_NAME}=${encodeConsentCookieValue(stored)}; ` +
            `Path=/; Max-Age=${COOKIE_CONSENT_MAX_AGE_SECONDS}; SameSite=Lax; Secure`;
}
//# sourceMappingURL=cookie.js.map